import { DOCUMENT } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationCancel, NavigationError, Router } from '@angular/router';
import { RefreshPromptDialogComponent } from '@app/components/refresh-prompt-dialog/refresh-prompt-dialog.component';
import { CacheService } from '@app/services/cache.service';
import { GITVERSION } from '@environments/git-version';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Utilities } from '@app/common/utilities';
import { WINDOW } from '@app/common/app-tokens';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  shouldLoadMainComponent = true;

  constructor(@Inject(LOCALE_ID) private localeId: string,
              @Inject(DOCUMENT) private document: Document,
              @Inject(WINDOW) private window: Window,
              private renderer: Renderer2,
              private cacheService: CacheService,
              private router: Router,
              private snackBar: MatSnackBar,
              private dialog: MatDialog) {
    if (this.localeId) {
      const twoLetterLangCode = localeId.split('-')[0];
      this.renderer.setAttribute(this.document.body.parentElement, 'lang', twoLetterLangCode);
    }

    this.clearCacheIfAppWasUpdated();
    this.shouldLoadMainComponent = Utilities.shouldLoadMain(this.window);
  }

  private clearCacheIfAppWasUpdated() {
    // Get currently stored version from cache
    const currentStoredVersion = this.cacheService.get<any>(GITVERSION.hash);
    if (!currentStoredVersion) {
      // If we dont have an entry for the stored version we set the current version.
      this.cacheService.clearPersistentCacheFromCoreWP();
      this.cacheService.set<any>(GITVERSION.hash, GITVERSION);
    } else {
      // If we have an entry for the version we compare it to the current version
      if (GITVERSION.hash !== currentStoredVersion.hash) {
        // If the current version differs from the stored version we clear the cache update the entry with the current version
        this.cacheService.clearPersistentCacheFromCoreWP();
        this.cacheService.set<any>(GITVERSION.hash, GITVERSION);
      } else {
        this.cacheService.removeAllExpiredItemsInPersistentCache();
      }
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((e): e is NavigationError | NavigationCancel => e instanceof NavigationError || e instanceof NavigationCancel))
        .subscribe((e) => {
          if (e instanceof NavigationError) {
            if (e?.error?.name === 'ChunkLoadError') {
              // ChunkLoadError indicates that we cant load a lazy loaded module for instance
              // Show dialog, prompting for refresh. Could be network/server issue
              this.dialog.open(RefreshPromptDialogComponent);
            }
          }
          if (e instanceof NavigationCancel) {
            if (e?.reason === '') {
              // When we've lost connection to the server it seems that navigation events gets cancelled with no reason. So we try and catch these.
              // Show dialog, prompting for refresh. Could be network/server issue
              this.dialog.open(RefreshPromptDialogComponent);
            }
            if (e?.reason.indexOf('unlicensed') >= 0) {
              Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.unlicensed);
            }
          }
        }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
